import React, { useEffect } from 'react'
import {
  Input,
  Button,
  Switch,
  message,
  Select,
  Cascader,
  Modal,
  Popover,
  Form
} from 'antd'
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  RightOutlined
} from '@ant-design/icons'
import {
  WatermarkExtractionStatus,
  WatermarkItem
} from '../../context/WatermarkBatchContext'
import { useWatermarkBatchContext } from '../../context/WatermarkBatchContext'
import { useWatermarkContent } from '../../hooks/useWatermarkContent'
import { useWatermarkApply } from '../../hooks/useWatermarkApply'
import BatchApplyDrawer from './BatchApplyDrawer'
import WatermarkProcessModal from './WatermarkProcessModal'
import MembershipTierLimitsModal from '../MembershipTierLimitsModal'
import {
  useWatermarkBatchEditorTrack,
  calculateItemChanges,
  calculatePhotoStats
} from '../../hooks/useWatermarkBatchEditorTrack'
import {
  getFieldDisabledStatus,
  isAllItemsDisabled as checkIsAllItemsDisabled
} from '../../hooks/useWatermarkItemEditStatus'
import {
  useMembershipTierLimit,
  shouldShowMembershipLimitModal
} from '../../hooks/useMembershipTierLimit'
import { useVip, usePayment, useAppSelector } from '../../../../../app/hooks'
import CONFIG from '../../../../../constants/config'

import './WatermarkEditPanel.scss'

declare const __RELEASE_STAGE__: string

const { TextArea } = Input

const { Option } = Select

// 自定义禁用输入框组件（支持多行，但不显示动态高度样式）
const DisabledTextArea: React.FC<{
  value: string
  placeholder?: string
  className?: string
}> = ({ value, placeholder, className }) => {
  return (
    <div className={`disabled-textarea ${className || ''}`}>
      {value || (
        <span className="disabled-textarea-placeholder">{placeholder}</span>
      )}
    </div>
  )
}

const WatermarkEditPanel: React.FC = () => {
  const {
    photoList,
    currentIndex,
    currentPhoto,
    updatePhotosStatus,
    isPhotoSelectable,
    watermarkCnt,
    getSelectedPhotos,
    setHasUnsavedChanges,
    setUploadModalVisible
  } = useWatermarkBatchContext()
  const [items, setItems] = React.useState<WatermarkItem[]>([])
  const [showBatchApplyDrawer, setShowBatchApplyDrawer] =
    React.useState<boolean>(false)
  const [showMembershipLimitModal, setShowMembershipLimitModal] =
    React.useState<boolean>(false)
  const [pendingApplyAction, setPendingApplyAction] = React.useState<{
    type: 'current' | 'selected' | 'all'
    items: WatermarkItem[]
    selectedPhotos?: any[]
  } | null>(null)

  const [newItems, setNewItems] = React.useState<WatermarkItem[]>([])
  const [modifiedItems, setModifiedItems] = React.useState<
    Array<{
      item: WatermarkItem
      originalContent: string
    }>
  >([])
  const [deletedItems, setDeletedItems] = React.useState<WatermarkItem[]>([])

  // 使用埋点 hook
  const { trackWatermarkBatchEditor } = useWatermarkBatchEditorTrack()

  // 获取会员状态
  const { isVip, isSVip, isVVip } = useVip()
  const payment = usePayment()

  // 获取用户和团队信息，用于生成问卷链接
  const user = useAppSelector((state) => state.main.user)
  const group = useAppSelector((state) => state.group)

  // 从提示弹窗打开会员权益弹窗的状态
  const [showMembershipLimitModalFromTip, setShowMembershipLimitModalFromTip] =
    React.useState<boolean>(false)

  // Popover 显示状态
  const [tipPopoverVisible, setTipPopoverVisible] =
    React.useState<boolean>(false)

  // 获取当前会员等级和限制信息
  const currentMembershipInfo = React.useMemo(() => {
    const vipKind = group?.vipInfo?.vip_kind ?? null
    const isVipStatus = group?.isVip ?? false

    if (!isVipStatus) {
      return {
        tier: '非会员',
        limit: 0,
        tierKey: 'none' as const
      }
    }

    switch (vipKind) {
      case CONFIG.VIP_KIND.SVIP: // 2 - 超级会员
        return {
          tier: '超级会员',
          limit: 100,
          tierKey: 'svip' as const
        }
      case CONFIG.VIP_KIND.VVIP: // 3 - 高级会员
        return {
          tier: '高级会员',
          limit: 20,
          tierKey: 'vvip' as const
        }
      case CONFIG.VIP_KIND.VIP: // 1 - 基础会员
        return {
          tier: '基础会员',
          limit: 1,
          tierKey: 'vip' as const
        }
      default:
        return {
          tier: '非会员',
          limit: 0,
          tierKey: 'none' as const
        }
    }
  }, [group])

  // 生成带参数的问卷链接
  const getSurveyUrl = () => {
    const userID = user?.user_id || ''
    const nickname = user?.nickname || ''
    const inviteID = group ? group.invite_id || group.groupId || '' : ''
    const groupName = group ? group.groupName || '' : ''
    return `https://alidocs.dingtalk.com/notable/share/form/v013BMqYybKYzKb6qwZ_hERWDMS_WyPvuh8?prefill_UserID=${encodeURIComponent(
      userID || ''
    )}&prefill_%E5%A7%93%E5%90%8D=${encodeURIComponent(
      nickname || ''
    )}&prefill_%E5%9B%A2%E9%98%9F%E5%8F%B7=${encodeURIComponent(
      inviteID || ''
    )}&prefill_%E5%9B%A2%E9%98%9F%E5%90%8D=${encodeURIComponent(
      groupName || ''
    )}`
  }

  // 计算照片统计
  const { allPhotoCount, uneditedCount, failedExtractionCount } =
    calculatePhotoStats(photoList)

  // 处理会员限制弹窗关闭
  const handleMembershipLimitModalCancel = () => {
    setShowMembershipLimitModal(false)
    setPendingApplyAction(null)
  }

  // 处理会员限制弹窗确认（继续执行应用）
  const handleMembershipLimitModalConfirm = (limit: number) => {
    setShowMembershipLimitModal(false)

    // 如果限制为0（非会员），不执行应用操作
    if (limit === 0) {
      setPendingApplyAction(null)
      return
    }

    if (pendingApplyAction) {
      // 执行待执行的应用操作
      const { type, items: pendingItems, selectedPhotos } = pendingApplyAction
      setPendingApplyAction(null)

      // 根据限制数量截取要处理的照片
      let limitedSelectedPhotos = selectedPhotos
      if (
        type === 'selected' &&
        selectedPhotos &&
        selectedPhotos.length > limit
      ) {
        // 只处理限制数量的照片
        limitedSelectedPhotos = selectedPhotos.slice(0, limit)
      } else if (type === 'all' && allPhotoCount > limit) {
        // 对于"应用到全部"，需要从 photoList 中截取限制数量的照片
        // 但由于 handleApplyWatermark 内部处理 all 类型时会过滤掉当前照片，
        // 我们需要特殊处理：如果限制数量小于总数量，改为 selected 类型
        const validPhotos = photoList.filter(
          (photo, index) => index !== currentIndex
        )
        if (validPhotos.length > limit) {
          limitedSelectedPhotos = validPhotos.slice(0, limit)
          // 改为 selected 类型来处理限制数量的照片
          handleApplyWatermark('selected', pendingItems, limitedSelectedPhotos)
          return
        }
      }

      handleApplyWatermark(type, pendingItems, limitedSelectedPhotos)
    }
  }

  // 检查并处理会员限制的函数
  const checkAndHandleMembershipLimit = (
    photoCount: number,
    applyType: 'current' | 'selected' | 'all',
    items: WatermarkItem[],
    selectedPhotos?: any[],
    onConfirmCallback?: () => void // 确认后的回调函数
  ): boolean => {
    // 非会员不能使用批量编辑功能，需要显示限制弹窗
    if (!isVip) {
      setPendingApplyAction({ type: applyType, items, selectedPhotos })
      setShowMembershipLimitModal(true)
      return false // 阻止继续执行
    }

    const shouldShow = shouldShowMembershipLimitModal(
      photoCount,
      isVip,
      isSVip,
      isVVip
    )

    if (shouldShow) {
      // 需要显示限制弹窗
      setPendingApplyAction({ type: applyType, items, selectedPhotos })
      setShowMembershipLimitModal(true)
      return false // 阻止继续执行
    }

    return true // 可以继续执行
  }

  // 使用水印应用 hook
  const {
    loading,
    handleApplyWatermark,
    resultModal,
    closeResultModal,
    processingPhotoCount
  } = useWatermarkApply({
    photoList,
    currentIndex,
    currentPhoto,
    updatePhotosStatus
  })

  // 使用水印内容hook
  const { fetchWatermarkContent } = useWatermarkContent()

  // 当照片变化时，获取水印内容并重置编辑状态
  useEffect(() => {
    // 切换照片时重置编辑状态
    setHasUnsavedChanges(false)

    // 只有当currentPhoto存在且有水印数据时才获取水印内容
    if (currentPhoto?.watermarkData && currentPhoto.watermarkData.length > 0) {
      // 检查是否所有项都已经有 contentEdit（应用后保留的数据）
      const allItemsHaveContentEdit = currentPhoto.watermarkData.every(
        (item) => item.contentEdit
      )

      if (allItemsHaveContentEdit && currentPhoto.watermarkID) {
        // 如果所有项都已经有 contentEdit，直接使用，不再请求接口
        // 但仍需要调用 fetchWatermarkContent 来同步最新的模板配置（如 options）
        // 但确保不会覆盖已有的 contentEdit
        fetchWatermarkContent(
          currentPhoto.watermarkID || '',
          currentPhoto.watermarkData
        )
          .then((updatedWatermarkData) => {
            // 更新items状态
            setItems(updatedWatermarkData)
          })
          .catch((error) => {
            console.error('获取水印内容失败:', error)
            // 如果请求失败，使用原有的数据
            setItems(currentPhoto.watermarkData || [])
          })
      } else if (currentPhoto.watermarkID) {
        // 如果有 watermarkID 但部分项缺少 contentEdit，请求接口获取
        fetchWatermarkContent(
          currentPhoto.watermarkID || '',
          currentPhoto.watermarkData
        )
          .then((updatedWatermarkData) => {
            // 更新items状态
            setItems(updatedWatermarkData)
          })
          .catch((error) => {
            console.error('获取水印内容失败:', error)
          })
      } else {
        // 如果没有 watermarkID，直接使用现有的 watermarkData
        setItems(currentPhoto.watermarkData)
      }
    } else {
      // 如果没有水印数据，清空items
      setItems([])
    }
  }, [
    currentPhoto?.watermarkID,
    currentPhoto?.watermarkData,
    fetchWatermarkContent,
    setHasUnsavedChanges
  ])

  /**
   * 显示批量应用抽屉
   */
  const handleShowBatchApplyDrawerClick = () => {
    // 对比当前照片的水印数据和编辑中的水印项，找出新增、修改和删除的内容
    const currentWatermarkData = currentPhoto?.watermarkData || []
    const editItems = items || []

    // 使用工具函数计算变更
    const changes = calculateItemChanges(currentWatermarkData, editItems)

    // 更新状态
    setNewItems(changes.newItems)
    setModifiedItems(changes.modifiedItems)
    setDeletedItems(changes.deletedItems)
    setShowBatchApplyDrawer(true)

    // 埋点：显示应用到全部照片确认弹窗
    trackWatermarkBatchEditor({
      actionType: 'showApplyToAllPhotosPop',
      number1: watermarkCnt,
      number2: allPhotoCount,
      number3: uneditedCount,
      number4: failedExtractionCount,
      photoNum: allPhotoCount,
      WatermarkID: currentPhoto?.watermarkID,
      baseID: currentPhoto?.watermarkID, // baseID 可能需要从其他地方获取
      string1: changes.newItemsDetail,
      string2: changes.modifiedItemsDetail,
      string3: changes.deletedItemsDetail
    })
  }

  // 处理取消
  const handleCancelBatchApplyDrawer = () => {
    // 埋点：取消应用到全部照片
    const currentWatermarkData = currentPhoto?.watermarkData || []
    const changes = calculateItemChanges(currentWatermarkData, items)

    trackWatermarkBatchEditor({
      actionType: 'cancelApplyToAllPhotosPop',
      number1: watermarkCnt,
      number2: allPhotoCount,
      number3: uneditedCount,
      number4: failedExtractionCount,
      photoNum: allPhotoCount,
      WatermarkID: currentPhoto?.watermarkID,
      baseID: currentPhoto?.watermarkID,
      string1: changes.newItemsDetail,
      string2: changes.modifiedItemsDetail,
      string3: changes.deletedItemsDetail
    })

    setShowBatchApplyDrawer(false)
  }

  // 更新水印项
  const handleUpdateItem = (
    item_id: string | number,
    field: string,
    value: any
  ) => {
    const newItems = [...items]
    const index = newItems.findIndex((item) => item.item_id === item_id)
    if (index !== -1) {
      newItems[index] = {
        ...newItems[index],
        [field]: value
      }
      setItems(newItems)
      // 标记为已修改
      setHasUnsavedChanges(true)
    }
  }

  // 添加自定义项
  const handleAddCustomItem = () => {
    const timestamp = Date.now() // NUMBER 类型的时间戳
    const newItem: WatermarkItem = {
      id: timestamp.toString(),
      item_id: timestamp.toString(), // NUMBER 类型
      title: '',
      content: '',
      switch: true
    } as WatermarkItem & { userCustom?: boolean }
    // 添加 userCustom 属性（即使类型定义中没有）

    ;(newItem as any).userCustom = true
    setItems([...items, newItem])

    // 添加自定义项时标记为已修改
    // 注意：即使内容为空，添加操作本身也算修改
    setHasUnsavedChanges(true)

    // 埋点：添加自定义项
    trackWatermarkBatchEditor({
      actionType: 'addItem',
      number1: watermarkCnt,
      number2: allPhotoCount,
      number3: uneditedCount,
      number4: failedExtractionCount,
      photoNum: allPhotoCount,
      WatermarkID: currentPhoto?.watermarkID,
      itemID: timestamp.toString()
    })
  }

  // 检查当前照片是否可以选择/编辑
  if (currentPhoto && !isPhotoSelectable(currentPhoto)) {
    const isFailed =
      currentPhoto.watermarkStatus === WatermarkExtractionStatus.FAILED
    const isExtractingTimeout =
      currentPhoto.watermarkStatus ===
      WatermarkExtractionStatus.EXTRACTING_TIMEOUT
    const isExtracting =
      currentPhoto.watermarkStatus === WatermarkExtractionStatus.EXTRACTING

    // 如果是提取超时，不允许编辑
    if (isExtractingTimeout) {
      return (
        <div className="watermarkEditPanel">
          <div className="panelHeader">
            <h3>编辑水印</h3>
          </div>

          <Form layout="vertical">
            <div className="watermarkItems">
              <div className="noWatermarkMessage">
                <ClockCircleOutlined className="messageIcon warning" />
                <span>水印提取超时，无法编辑</span>
              </div>
            </div>
          </Form>
        </div>
      )
    }

    // 如果是提取失败，不允许编辑
    if (isFailed) {
      return (
        <div className="watermarkEditPanel">
          <div className="panelHeader">
            <h3>编辑水印</h3>
          </div>

          <Form layout="vertical">
            <div className="watermarkItems">
              <div className="noWatermarkMessage">
                <ExclamationCircleOutlined className="messageIcon error" />
                <span>该照片水印提取失败，无法编辑</span>
              </div>
            </div>
          </Form>
        </div>
      )
    }

    // 如果是正在提取中，显示提取中提示
    if (isExtracting) {
      return (
        <div className="watermarkEditPanel">
          <div className="panelHeader">
            <h3>编辑水印</h3>
          </div>

          <Form layout="vertical">
            <div className="watermarkItems">
              <div className="noWatermarkMessage">
                <LoadingOutlined className="messageIcon loading" />
                <span>
                  正在AI提取水印信息
                  <br />
                  提取完成后，可编辑水印
                </span>
              </div>
            </div>
          </Form>
        </div>
      )
    }
  }

  // 根据editType渲染不同类型的编辑组件
  const renderEditComponent = (item: WatermarkItem) => {
    const { contentEdit, content, item_id, edit_type } = item
    const disabledStatus = getFieldDisabledStatus(edit_type)

    if (!contentEdit) {
      // 如果没有contentEdit，默认使用输入框
      // 如果content包含"请输入"，则显示为空值，让placeholder显示提示
      const inputValue = content && content.includes('请输入') ? '' : content
      if (disabledStatus.content) {
        return (
          <DisabledTextArea
            value={inputValue}
            placeholder={`请输入${item.title}` || ''}
          />
        )
      }
      return (
        <TextArea
          autoSize
          placeholder={`请输入${item.title}` || ''}
          value={inputValue}
          onChange={(e) => handleUpdateItem(item_id, 'content', e.target.value)}
        />
      )
    }

    const { editType, itemsList, isMultiChoice } = contentEdit

    switch (editType) {
      case 0: // 输入框，员工手动输入
        if (disabledStatus.content) {
          return (
            <DisabledTextArea value={content} placeholder={item.title || ''} />
          )
        }
        return (
          <TextArea
            autoSize
            placeholder={item.title || ''}
            value={content}
            onChange={(e) =>
              handleUpdateItem(item_id, 'content', e.target.value)
            }
          />
        )

      case 1: // 固定内容，员工不可修改（不可修改的输入框）
        return (
          <DisabledTextArea value={content} placeholder={item.title || ''} />
        )

      case 2: // 预设选项，让员工选择（下拉选项，支持单选和多选）
        // 将content字符串按'、'分割成数组，用于多选模式的value
        let selectedValues = content ? content.split('、\b') : []

        return (
          <Select
            mode={isMultiChoice ? 'multiple' : undefined}
            placeholder={`请选择${item.title}`}
            value={isMultiChoice ? selectedValues : content}
            onChange={(value) => {
              // 如果是多选模式，将选中的数组用'、'连接成字符串
              const newContent =
                isMultiChoice && Array.isArray(value)
                  ? value.join('、\b')
                  : value
              handleUpdateItem(item_id, 'content', newContent)
            }}
            style={{ width: '100%' }}
            disabled={disabledStatus.content}
          >
            {itemsList &&
              itemsList.map((option: string, index: number) => (
                <Option key={index} value={option}>
                  {option}
                </Option>
              ))}
          </Select>
        )

      case 3: // 预设多级选项，让员工选择（下拉选项，多选）
        // 获取选项数据
        const options = item.options || []

        // 将options数据转换为Cascader组件需要的格式
        const transformOptionsToCascaderData = (options: any[]) => {
          const cascaderData: any[] = []

          // 遍历每个option的records数组
          options.forEach((option) => {
            if (option.records && option.records.length > 0) {
              option.records.forEach((record: any) => {
                const cascaderOption = transformRecordToCascaderOption(record)
                cascaderData.push(cascaderOption)
              })
            }
          })

          return cascaderData
        }

        // 递归函数，将record转换为Cascader选项
        const transformRecordToCascaderOption = (record: any) => {
          const option: any = {
            label: record.content,
            value: record.content
          }

          if (record.childs && record.childs.length > 0) {
            option.children = record.childs.map((child: any) => {
              return transformRecordToCascaderOption(child)
            })
          }

          return option
        }

        // 转换options为Cascader组件需要的格式
        const cascaderOptions = transformOptionsToCascaderData(options)

        // 解析当前选中的值
        let selectedValue: string[] = []
        if (content) {
          // 处理接口返回的content格式，如"施工部-\b技术-\b砌砖"
          if (content.includes('-\b')) {
            selectedValue = content.split('-\b')
          } else if (content.includes('、\b')) {
            selectedValue = content.split('、\b')
          } else {
            // 如果不是标准格式，尝试从content中解析出选中的路径
            const findPathByValue = (
              options: any[],
              value: string,
              path: string[] = []
            ): string[] | null => {
              for (const option of options) {
                const newPath = [...path, option.value]
                if (option.value === value) {
                  return newPath
                }
                if (option.children && option.children.length > 0) {
                  const result = findPathByValue(
                    option.children,
                    value,
                    newPath
                  )
                  if (result) {
                    return result
                  }
                }
              }
              return null
            }

            // 假设content是最后一项的值，尝试找到完整路径
            selectedValue = findPathByValue(cascaderOptions, content) || []
          }
        }

        // 处理级联选择器的选择事件
        const handleCascaderChange = (value: string[]) => {
          if (value && value.length > 0) {
            // 对于多级选项(Cascader)，使用'-\b'作为分隔符
            const formattedValue = value.join('-\b')
            handleUpdateItem(item_id, 'content', formattedValue)
          } else {
            handleUpdateItem(item_id, 'content', '')
          }
        }

        return (
          <Cascader
            options={cascaderOptions}
            value={selectedValue}
            onChange={handleCascaderChange}
            placeholder={`请选择${item.title}`}
            style={{ width: '100%' }}
            changeOnSelect={false}
            displayRender={(value: string[]) => value.join('-\b')}
            showSearch
            allowClear
            disabled={disabledStatus.content}
          />
        )

      default:
        if (disabledStatus.content) {
          return (
            <DisabledTextArea value={content} placeholder={item.title || ''} />
          )
        }
        return (
          <TextArea
            autoSize
            placeholder={item.title || ''}
            value={content}
            onChange={(e) =>
              handleUpdateItem(item_id, 'content', e.target.value)
            }
          />
        )
    }
  }

  // 判断是否所有item都不支持编辑
  const isAllItemsDisabled = checkIsAllItemsDisabled(items)

  return (
    <div className="watermarkEditPanel">
      <div className="panelHeader">
        <div className="panelTitle">
          <h3>编辑水印</h3>
          <Popover
            content={
              <div className="watermark-edit-tip-content">
                <div className="tip-title">编辑说明</div>
                <div className="tip-list">
                  <div className="tip-item">
                    • 编辑水印后，点击"应用"按钮可将修改应用到照片
                  </div>
                  <div className="tip-item">
                    • 支持批量应用到当前照片、选中照片或全部照片
                  </div>
                  <div className="tip-item">
                    • 可以添加自定义项来补充水印信息
                  </div>
                  <div className="tip-item">
                    •
                    注意：暂不支持显示高亮、主题色、透明度、无遮挡、大小等编辑和预览
                  </div>
                </div>
                <div className="tip-divider"></div>
                <div className="tip-membership-info">
                  <div className="membership-row">
                    <span className="membership-label">当前等级：</span>
                    <span
                      className={`membership-value membership-value--tier membership-value--tier-${currentMembershipInfo.tierKey}`}
                    >
                      {currentMembershipInfo.tier}
                    </span>
                  </div>
                  <div className="membership-row">
                    <span className="membership-label">单次编辑最大支持：</span>
                    <span
                      className={`membership-value ${
                        currentMembershipInfo.limit > 0
                          ? 'membership-value--limit'
                          : 'membership-value--limit-unsupported'
                      }`}
                    >
                      {currentMembershipInfo.limit > 0
                        ? `${currentMembershipInfo.limit}张`
                        : '不支持'}
                    </span>
                  </div>
                  <div
                    className="membership-link"
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      setTipPopoverVisible(false)
                      setShowMembershipLimitModalFromTip(true)
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        setTipPopoverVisible(false)
                        setShowMembershipLimitModalFromTip(true)
                      }
                    }}
                  >
                    <span>查看更多会员权益</span>
                    <RightOutlined className="link-arrow" />
                  </div>
                </div>
              </div>
            }
            title={null}
            trigger="click"
            placement="bottomLeft"
            classNames={{ root: 'watermark-edit-tip-popover' }}
            open={tipPopoverVisible}
            onOpenChange={setTipPopoverVisible}
          >
            <ExclamationCircleOutlined className="watermark-edit-tip-icon" />
          </Popover>
        </div>
        {currentPhoto?.watermarkStatus !==
          WatermarkExtractionStatus.EXTRACTING &&
          currentPhoto?.watermarkStatus !== WatermarkExtractionStatus.FAILED &&
          !isAllItemsDisabled && (
            <Button
              type="link"
              className="addCustomItemBtn"
              onClick={handleAddCustomItem}
            >
              添加自定义项
            </Button>
          )}
      </div>

      {/* 无修改项提示 */}
      {currentPhoto?.watermarkStatus ===
        WatermarkExtractionStatus.EDIT_NO_CHANGES && (
        <div className="no-changes-notice">
          <InfoCircleOutlined className="no-changes-notice-icon" />
          <span className="no-changes-notice-text">
            上次应用未在当前图片中找到需要处理的内容
            <br />
            可添加自定义项进行编辑
          </span>
        </div>
      )}

      {/* 超时提示 */}
      {currentPhoto?.watermarkStatus ===
        WatermarkExtractionStatus.EDIT_TIMEOUT && (
        <div className="timeout-notice">
          <ClockCircleOutlined className="timeout-notice-icon" />
          <span className="timeout-notice-text">上次编辑超时，请重新编辑</span>
        </div>
      )}

      <Form layout="vertical">
        <div className="watermarkItems">
          {items && items.length > 0 ? (
            <>
              {/* 有水印项时显示水印列表 */}
              {items.map((item) => {
                // 判断是否为自定义项
                const isCustomItem = (item as any).userCustom === true
                // 获取字段禁用状态

                const disabledStatus = getFieldDisabledStatus(item.edit_type)

                return (
                  <div key={item.item_id} className="watermarkItem">
                    <div className="itemControl">
                      <Switch
                        checked={item.switch}
                        onChange={(checked) => {
                          handleUpdateItem(item.item_id, 'switch', checked)

                          // 埋点：点击关闭/打开条目
                          trackWatermarkBatchEditor({
                            actionType: checked
                              ? 'clickOpenItem'
                              : 'clickCloseItem',
                            number1: watermarkCnt,
                            number2: allPhotoCount,
                            number3: uneditedCount,
                            number4: failedExtractionCount,
                            photoNum: allPhotoCount,
                            WatermarkID: currentPhoto?.watermarkID,
                            itemID: String(item.item_id)
                          })
                        }}
                        disabled={disabledStatus.switch}
                      />
                    </div>
                    <div className="itemLabel">
                      {isCustomItem || !disabledStatus.title ? (
                        <Input
                          value={item.title}
                          onChange={(e) =>
                            handleUpdateItem(
                              item.item_id,
                              'title',
                              e.target.value
                            )
                          }
                          placeholder={'请输入标题'}
                          className="custom-item-title-input"
                          disabled={disabledStatus.title}
                        />
                      ) : (
                        item.title
                      )}
                    </div>
                    <div className="itemContent">
                      {renderEditComponent(item)}
                    </div>
                  </div>
                )
              })}
              {/* 如果所有item都不支持编辑，显示提示信息 */}
              {isAllItemsDisabled && (
                <div className="allItemsDisabledNotice">
                  <span className="warningText">当前照片无可编辑水印项，</span>
                  <span
                    className="addCustomItemLink"
                    onClick={handleAddCustomItem}
                  >
                    可添加自定义项
                  </span>
                </div>
              )}
            </>
          ) : (
            // 没有水印项时显示提示信息
            <div className="noWatermarkMessage">
              {currentPhoto ? (
                <>
                  <span className="warningText">当前照片无可编辑水印项，</span>
                  <span
                    className="addCustomItemLink"
                    onClick={handleAddCustomItem}
                  >
                    可添加自定义项
                  </span>
                </>
              ) : (
                '请选择一张支持编辑水印的图片'
              )}
            </div>
          )}
        </div>
      </Form>

      {items && items.length > 0 && (
        <div className="actionButtons">
          <Button
            type="primary"
            onClick={handleShowBatchApplyDrawerClick}
            loading={loading}
          >
            批量应用
          </Button>
          <Button
            type="primary"
            ghost
            onClick={() => {
              // 埋点：点击应用到当前照片
              const currentWatermarkData = currentPhoto?.watermarkData || []
              const changes = calculateItemChanges(currentWatermarkData, items)

              trackWatermarkBatchEditor({
                actionType: 'clickApplyToCurrentPhoto',
                number1: watermarkCnt,
                number2: allPhotoCount,
                number3: uneditedCount,
                number4: failedExtractionCount,
                photoNum: allPhotoCount,
                WatermarkID: currentPhoto?.watermarkID,
                baseID: currentPhoto?.watermarkID,
                bool1: changes.hasModify,
                bool2: changes.hasAdd,
                bool3: changes.hasDelete
              })

              // 检查会员限制（当前图片始终是1张，通常不会超过限制，但为了统一处理也检查）
              if (
                !checkAndHandleMembershipLimit(
                  1,
                  'current',
                  items,
                  undefined,
                  () => {
                    // 重置编辑状态（用户已确认应用）
                    setHasUnsavedChanges(false)
                    handleApplyWatermark('current', items)
                  }
                )
              ) {
                return
              }

              // 重置编辑状态（用户已确认应用）
              setHasUnsavedChanges(false)
              handleApplyWatermark('current', items)
            }}
            loading={loading}
          >
            应用到当前图片
          </Button>
        </div>
      )}

      {/* 批量应用侧边面板 */}
      <BatchApplyDrawer
        visible={showBatchApplyDrawer}
        onClose={handleCancelBatchApplyDrawer}
        newItems={newItems}
        modifiedItems={modifiedItems}
        deletedItems={deletedItems}
        currentPhoto={currentPhoto}
        onApply={(selectedPhotos) => {
          setShowBatchApplyDrawer(false)
          // 埋点：点击应用到选中照片
          const currentWatermarkData = currentPhoto?.watermarkData || []
          const changes = calculateItemChanges(currentWatermarkData, items)

          trackWatermarkBatchEditor({
            actionType: 'clickApplyToSelectedPhotos',
            number1: watermarkCnt,
            number2: allPhotoCount,
            number3: uneditedCount,
            number4: failedExtractionCount,
            photoNum: selectedPhotos.length,
            WatermarkID: currentPhoto?.watermarkID,
            baseID: currentPhoto?.watermarkID,
            bool1: changes.hasModify,
            bool2: changes.hasAdd,
            bool3: changes.hasDelete
          })

          // 检查会员限制
          if (
            !checkAndHandleMembershipLimit(
              selectedPhotos.length,
              'selected',
              items,
              selectedPhotos,
              () => {
                // 重置编辑状态（用户已确认应用）
                setHasUnsavedChanges(false)
                handleApplyWatermark('selected', items, selectedPhotos)
              }
            )
          ) {
            return
          }

          // 重置编辑状态（用户已确认应用）
          setHasUnsavedChanges(false)
          handleApplyWatermark('selected', items, selectedPhotos)
        }}
        loading={loading}
      />

      {/* 照片处理进度/结果弹窗（统一组件） */}
      <WatermarkProcessModal
        visible={loading || resultModal.visible}
        mode={loading ? 'processing' : 'result'}
        photoCount={loading ? processingPhotoCount : undefined}
        successCount={resultModal.successCount}
        successPhotos={resultModal.successPhotos}
        noChangesCount={resultModal.noChangesCount}
        noChangesPhotos={resultModal.noChangesPhotos}
        timeoutCount={resultModal.timeoutCount}
        timeoutPhotos={resultModal.timeoutPhotos}
        failedCount={resultModal.failedCount}
        failedPhotos={resultModal.failedPhotos}
        failureReason={resultModal.failureReason}
        surveyUrl={getSurveyUrl()}
        onUploadToFolderClick={() => {
          if (!isSVip) {
            payment.openSvip('webBatchEditWatermark', false, true, false)
          } else {
            closeResultModal()
            setUploadModalVisible(true)
          }
        }}
        onClose={() => {
          closeResultModal()
          // 关闭结果弹窗时，如果有成功应用，重置编辑状态
          if (resultModal.successCount > 0 || resultModal.noChangesCount > 0) {
            setHasUnsavedChanges(false)
          }
        }}
      />

      {/* 会员权益限制弹窗 */}
      {pendingApplyAction && (
        <MembershipTierLimitsModal
          visible={showMembershipLimitModal}
          photoCount={
            pendingApplyAction.type === 'current'
              ? 1
              : pendingApplyAction.type === 'selected'
                ? pendingApplyAction.selectedPhotos?.length || 0
                : allPhotoCount
          }
          onCancel={handleMembershipLimitModalCancel}
          onConfirm={handleMembershipLimitModalConfirm}
        />
      )}

      {/* 从提示弹窗打开的会员权益弹窗 */}
      <MembershipTierLimitsModal
        visible={showMembershipLimitModalFromTip}
        photoCount={allPhotoCount}
        onCancel={() => setShowMembershipLimitModalFromTip(false)}
      />
    </div>
  )
}

export default WatermarkEditPanel
